import { useEffect, useState } from "react";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";

/** Personal canvas appearance; never changes sheet contents or export colours. */
export function useCanvasAppearance(editor: Editor, room: { id: string }) {
  const [view, setView] = useState(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(`planroom:view:${room.id}`) || "null",
      );
      return {
        spacing: Number.isFinite(saved?.spacing)
          ? Math.max(4, Math.min(100, saved.spacing))
          : 20,
        grid: typeof saved?.grid === "boolean" ? saved.grid : false,
        gridStyle: saved?.gridStyle === "dots" ? "dots" : "lines",
        color: /^#[0-9a-f]{6}$/i.test(saved?.color) ? saved.color : "#faf9f6",
      };
    } catch {
      return { spacing: 20, grid: false, gridStyle: "lines", color: "#faf9f6" };
    }
  });
  const camera = useValue("canvas camera", () => editor.getCamera(), [editor]);
  const gridSize = useValue(
    "canvas grid size",
    () => editor.getDocumentSettings().gridSize,
    [editor],
  );
  useEffect(() => {
    editor.updateInstanceState({ isGridMode: view.grid });
    const container = editor.getContainer();
    container.dataset.planroomGridStyle = view.grid ? view.gridStyle : "plain";
    // The SDK background token also paints text outlines and shape interiors.
    // A personal canvas colour must only affect the empty workspace.
    container.style.setProperty("--planroom-canvas-background", view.color);
    try {
      localStorage.setItem(`planroom:view:${room.id}`, JSON.stringify(view));
    } catch {}
  }, [view, editor, room.id]);
  useEffect(() => {
    const container = editor.getContainer();
    const base = view.spacing * editor.getZoomLevel();
    const step = base > 0 ? base * Math.max(1, Math.ceil(12 / base)) : 12;
    const origin = editor.pageToScreen({ x: 0, y: 0 });
    const rect = container.getBoundingClientRect();
    container.style.setProperty("--planroom-grid-step", `${step}px`);
    container.style.setProperty(
      "--planroom-grid-x",
      `${(origin.x - rect.left) % step}px`,
    );
    container.style.setProperty(
      "--planroom-grid-y",
      `${(origin.y - rect.top) % step}px`,
    );
  }, [editor, camera, gridSize, view.spacing]);
  return { view, setView };
}
export type CanvasAppearance = ReturnType<typeof useCanvasAppearance>;
